import { Component, inject, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { ResetForm } from '../cod/state/cod.action';
import { select, Store } from '@ngxs/store';
import { ButtonModule } from '@sag-ui';
import { CodOverviewTableComponent } from '../shared/components/cod-overview-table/cod-overview-table.component';
import { CodOverviewState } from './state/cod-overview.state';
import { debounceTime, distinctUntilChanged, filter, Observable, tap } from 'rxjs';
import { ResetCodReview } from '../cod/state/cod-review.action';
import {
    LazyLoadAllCods,
    LazyLoadMyCods,
    LazyLoadTasks,
    LoadAllCods,
    LoadMyCods,
    LoadTasks,
    SetFilter
} from './state/cod-overview.action';
import { GeneralCod } from '../cod/models/cod.model';
import { TableFilterComponent } from '../shared/components/table-filter/table-filter.component';
import { CodStatus, FinancialAdjustment, IUser, NatureOfComplaint, Region, Roles } from '@cod-monorepo/interfaces';
import { TableUserFilterComponent } from '../shared/components/table-user-filter/table-user-filter.component';
import { UserState } from '../user-management/state/user-management.state';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableCountryFilterComponent } from '../shared/components/table-country-filter/table-country-filter.component';
import { AsStringArrayPipe } from '../shared/pipes/as-string-array.pipe';

@Component({
    selector: 'app-home',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        TranslateModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        CodOverviewTableComponent,
        TableFilterComponent,
        TableUserFilterComponent,
        TableCountryFilterComponent,
        AsStringArrayPipe
    ],
    templateUrl: './home.component.html'
})
export class HomeComponent implements OnInit {
    public allCods$: Observable<GeneralCod[]> = inject(Store).select(CodOverviewState.allCods);
    public isLoadingAllCods$: Signal<boolean> = select(CodOverviewState.isLoadingAllCods);
    public allCodsLoaded$: Signal<boolean> = select(CodOverviewState.allCodsLoaded);
    public isLazyLoadingAllCods$: Signal<boolean> = select(CodOverviewState.isLazyLoadingAllCods);

    public myCods$: Observable<GeneralCod[]> = inject(Store).select(CodOverviewState.myCods);
    public isLoadingMyCods$: Signal<boolean> = select(CodOverviewState.isLoadingMyCods);
    public myCodsLoaded$: Signal<boolean> = select(CodOverviewState.myCodsLoaded);
    public isLazyLoadingMyCods$: Signal<boolean> = select(CodOverviewState.isLazyLoadingMyCods);

    public tasks$: Observable<GeneralCod[]> = inject(Store).select(CodOverviewState.tasks);
    public isLoadingTasks$: Signal<boolean> = select(CodOverviewState.isLoadingTasks);
    public tasksLoaded$: Signal<boolean> = select(CodOverviewState.tasksLoaded);
    public isLazyLoadingTasks$: Signal<boolean> = select(CodOverviewState.isLazyLoadingTasks);

    public filters: Signal<{ [key: string]: string[] | string }> = select(CodOverviewState.filters);
    public isAdmin: Signal<boolean | undefined> = select(UserState.isAdmin);

    public user$: Observable<IUser | undefined> = inject(Store).select(UserState.user);

    public selectedTab: 'my' | 'tasks' | 'all' = 'my';
    public natureOfComplaint: string[] = Object.values(NatureOfComplaint);
    public status: string[] = Object.values(CodStatus);
    public region: string[] = Object.values(Region);
    public financialAdjustment: string[] = Object.values(FinancialAdjustment);
    public searchControl = new FormControl(null);
    public isBasicUser = true;
    public canViewAllCods = false;
    constructor(private store: Store, private router: Router) {}

    ngOnInit(): void {
        this.initialLoad();
        this.initSearch();
    }

    public onTabChange(tab: 'my' | 'tasks' | 'all') {
        this.selectedTab = tab;
    }

    public createNewCod(): void {
        this.store.dispatch(new ResetForm());
        this.router.navigate(['/cod/create']);
    }

    public handleRowClick(cod: GeneralCod): void {
        this.store.dispatch(new ResetCodReview());
        this.router.navigate(['cod', cod.id, 'review']);
    }

    public lazyLoadAllCods(): void {
        this.store.dispatch(new LazyLoadAllCods());
    }

    public lazyLoadMyCods(): void {
        this.store.dispatch(new LazyLoadMyCods());
    }

    public lazyLoadTasks(): void {
        this.store.dispatch(new LazyLoadTasks());
    }

    private initialLoad(): void {
        this.user$
            .pipe(
                filter(user => !!user),
                tap(user => {
                    this.isBasicUser = user.roles.length === 1 && user.roles[0] === Roles.USER;
                    this.canViewAllCods = user.roles.includes(Roles.ADMIN) || user.roles.includes(Roles.REPORTING);

                    if (this.canViewAllCods) {
                        this.store.dispatch(new LoadAllCods());
                    }
                    this.store.dispatch(new LoadMyCods());
                    this.store.dispatch(new LoadTasks());

                    if (this.isBasicUser) {
                        this.selectedTab = 'my';
                    }
                })
            )
            .subscribe();
    }

    private loadCods(): void {
        if (this.canViewAllCods) {
            this.store.dispatch(new LoadAllCods());
        }
        this.store.dispatch(new LoadMyCods());
        this.store.dispatch(new LoadTasks());
    }

    public asStringArray(value: string | string[]): string[] {
        return Array.isArray(value) ? value : [value];
    }

    public asString(value: string | string[]): string {
        return Array.isArray(value) ? value[0] : value;
    }

    public setFilter(key: string, value: string | string[]): void {
        this.store.dispatch(new SetFilter({ [key]: value }));
        this.loadCods();
    }

    private initSearch(): void {
        this.searchControl.valueChanges
            .pipe(
                filter(value => value !== null),
                debounceTime(300),
                distinctUntilChanged(),
                tap(value => this.store.dispatch(new SetFilter({ search: value }))),
                tap(() => this.loadCods())
            )
            .subscribe();
    }
}
